import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectFileTypes } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectFileTypeState = {
    data: [],
    default: null,
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available file types for a given project database.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectFileTypes: async (replicaId: string): Promise<IProjectFileType[]> => {
        try {
            const url = urlProjectFileTypes.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectFileTypeState>) => {
        /**
         * Reducers for the 'getProjectFileTypes' thunk.
         */

        builder.addCase(getProjectFileTypes.pending, (state: Draft<IProjectFileTypeState>) => {
            state.data = null;
            state.default = null;
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectFileTypes.fulfilled,
            (state: Draft<IProjectFileTypeState>, action: IAction<IProjectFileType[]>) => {
                state.loading = false;
                state.error = null;
                state.default = action.payload.filter(x => x.name === '_')[0];
                state.data = action.payload.filter(x => x.name !== '_');
            },
        );
        builder.addCase(
            getProjectFileTypes.rejected,
            (state: Draft<IProjectFileTypeState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
                state.default = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectFileTypes = createAsyncThunk(
    'project/fileTypes',
    thunks.getProjectFileTypes,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export interface IProjectFileTypeState extends SlicePropsWithData<IProjectFileType[]> {
    /** Default/Fallback FileType configuration. */
    default: IProjectFileType;
}

/**
 * Interface that describes a FileType configuration.
 */
export interface IProjectFileType {
    /** FileType name. */
    name: string;

    /** FileType SVG Icon. */
    icon: string;

    /** FileType SVG Icon color. */
    color: string;

    /** Pipe-delimeted list of file extensions that this FileType should be matched to. */
    extensions: string;
}

/**
 * Create the redux slice.
 */
export const projectFileTypeSlice = createSlice({
    name: 'projectFileType',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducer as default.
 */
export default projectFileTypeSlice.reducer;
